import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  InjectionToken,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { has, isObject, reject } from 'lodash';
import {
  debounceTime,
  filter,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import {
  LookupResult,
  LookupResultItem,
  LookupResultSuccessItem,
  RetrieveResult,
  RetrieveResultItem,
} from './address-lookup.component.interface';

type LoqateConfig = {
  findLabel: string;
  retrieveLabel: string;
  serviceKey: string;
  findUrl: string;
  retrieveUrl: string;
  findErrorMessage: string;
  retrieveErrorMessage: string;
  placeholder: string;
};

export const LOQATE_CONFIG: InjectionToken<LoqateConfig> = new InjectionToken(
  'Loqate Address Lookup Config'
);

const addressValidator = (): ValidatorFn => {
  return (control: AbstractControl) => {
    return !isObject(control.value)
      ? { invalidAddress: 'invalid address message' }
      : null;
  };
};

@Component({
    selector: 'lui-address-lookup',
    templateUrl: './address-lookup.component.html',
    styleUrls: ['./address-lookup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddressLookupComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() label!: string;

  @Input() get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this._setValidators(this._required);
    this._cd.markForCheck();
  }

  _required = false;

  @Input() origin!: string | undefined;

  @Output() valueChange: EventEmitter<string> = new EventEmitter();

  @Output() addressSelected: EventEmitter<RetrieveResultItem> =
    new EventEmitter();

  findLabel!: string;

  retrieveLabel!: string;

  findErrorMessage!: string;

  retrieveErrorMessage!: string;

  placeholder!: string;

  readonly addressControl: FormControl = new FormControl('', {
    nonNullable: true,
    validators: [addressValidator()],
  });

  readonly addressControlValueChanges: Observable<string> =
    this.addressControl.valueChanges.pipe(filter((v) => typeof v === 'string'));

  readonly firstLineControl: FormControl = new FormControl('', {
    nonNullable: true,
  });

  readonly _firstLineSelectedSubject = new Subject<MatSelectChange>();

  firstLineSelectedStream: Observable<LookupResultSuccessItem> =
    this._firstLineSelectedSubject
      .asObservable()
      .pipe(map((selectEvent) => selectEvent.value));

  addressOptions$!: Observable<LookupResultSuccessItem[]>;

  firstLineOptions$!: Observable<LookupResultSuccessItem[]>;

  // private readonly fetchUrl!: string;
  private readonly retrieveUrl!: string;

  @ViewChild(MatAutocomplete) autocomplete!: MatAutocomplete;

  _destroy = new Subject<void>();

  get fetchUrl(): string {
    const baseUrl = new URL(`${this._config.findUrl}/json3.ws`);
    baseUrl.searchParams.append('Key', this._config.serviceKey);
    baseUrl.searchParams.append('IsMiddleware', 'false');

    this.origin && baseUrl.searchParams.append('ORIGIN', this.origin);
    return `${baseUrl.href}&Text=`;
  }

  constructor(
    @Inject(LOQATE_CONFIG) private _config: LoqateConfig,
    private readonly http: HttpClient,
    private _cd: ChangeDetectorRef
  ) {
    // this.fetchUrl = `${_config.findUrl}/json3.ws?Key=${_config.serviceKey}&IsMiddleware=false&Origin=NO&Text=`;
    this.retrieveUrl = `${_config.retrieveUrl}/json3.ws?Key=${_config.serviceKey}&Id=`;
    this.findLabel = _config.findLabel;
    this.retrieveLabel = _config.retrieveLabel;
    this.findErrorMessage = _config.findErrorMessage;
    this.retrieveErrorMessage = _config.retrieveErrorMessage;
    this.placeholder = _config.placeholder;
  }

  ngOnInit(): void {
    this.addressOptions$ = this.addressControl.valueChanges.pipe(
      filter((address: string) => address.length >= 3),
      debounceTime(200),
      switchMap((text: string) =>
        this.fetchLookup(text).pipe(map(this.filterResult.bind(this)))
      )
    );
  }

  ngAfterViewInit(): void {
    if (this.autocomplete) {
      this.addressSelectedStream()
        .pipe(
          filter((lookupRes) => lookupRes.Type === 'Address'),
          switchMap((address) => this.fetchRetrieveAddress(address.Id)),
          takeUntil(this._destroy)
        )
        .subscribe((res) => this.addressSelected.emit(res.Items[0]));

      this.firstLineOptions$ = merge(
        this.findAddressSelectedStream(),
        this.addressControlValueChanges
      ).pipe(
        filter(
          (lookupRes) =>
            typeof lookupRes === 'string' || lookupRes.Type === 'Container'
        ),
        switchMap((container) =>
          typeof container === 'string'
            ? of([])
            : this.fetchLookup('', container.Id).pipe(
                map(this.filterResult.bind(this))
              )
        )
      );
    }
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  markAsTouched() {
    this.addressControl.markAsTouched();
    this.firstLineControl.markAsTouched();
    this.addressControl.updateValueAndValidity();
    this._cd.markForCheck();
  }

  displayWith(value: LookupResultSuccessItem): string {
    return value ? value.Text : '';
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    const findValue = event.option.value as LookupResultSuccessItem;
    this.fetchRetrieveAddress(findValue.Text);
  }

  private findAddressSelectedStream(): Observable<LookupResultSuccessItem> {
    return this.autocomplete.optionSelected.pipe(
      map((event) => event.option.value as LookupResultSuccessItem)
    );
  }

  private addressSelectedStream(): Observable<LookupResultSuccessItem> {
    return merge(
      this.findAddressSelectedStream(),
      this.firstLineSelectedStream
    ).pipe(map((resultItem) => resultItem));
  }

  private fetchLookup(
    text: string,
    containerId?: string
  ): Observable<LookupResult> {
    const url = containerId
      ? `${this.fetchUrl}${text}&Container=${containerId}`
      : `${this.fetchUrl}${text}`;

    return this.http.post<LookupResult>(url, {});
  }

  private fetchRetrieveAddress(text: string): Observable<RetrieveResult> {
    return this.http.post<RetrieveResult>(`${this.retrieveUrl}${text}`, {});
  }

  private filterResult(result: LookupResult): LookupResultSuccessItem[] {
    return reject(result.Items, (item: LookupResultItem) =>
      has(item, 'Error')
    ) as LookupResultSuccessItem[];
  }

  private _setValidators(required: boolean) {
    if (required) {
      this.addressControl.addValidators([Validators.required]);
      this.firstLineControl.addValidators([Validators.required]);
    } else {
      this.addressControl.removeValidators([Validators.required]);
      this.firstLineControl.removeValidators([Validators.required]);
    }
    this.addressControl.updateValueAndValidity();
    this.firstLineControl.updateValueAndValidity();
  }
}
